import type { API } from '@imprint-payments/types';

import { URL } from '../api/apiConstants';
import { HEADERS } from '../api/http/httpConsts';
import { store } from '../app/store';
import { CardNetwork, IssuingBank } from './disclosures';
import { getEnv } from './environments';
import { LanguageCode } from './language';
import { DocumentUrl, MerchantQRInfo, ZendeskUrl } from './merchantLinks';

// The keyword contained in url
export enum MERCHANT_KEY {
  HH = 'horizonhobby',
  WG = 'westgate',
  HEB = 'heb',
  CM = 'centralmarket',
  HICV = 'holidayinnclub',
  TURKISH = 'turkishairlines',
  EB = 'eddiebauer',
  BB = 'brooksbrothers',
  FETCH = 'fetch',
  RAKUTEN = 'rakuten',
  BOOKING = 'booking',
  CRATE_BARREL = 'crateandbarrel',
  KROGER = 'kroger',
  SHELL = 'shell',
}

export const PRODUCT_UUID = {
  HH: 'PRDT-v1-785c001c-a9b1-4fc2-87d7-2d0b1060e3ed',
  WG: 'PRDT-v1-a0b163f2-21b1-4fcc-8106-660b6c456c71',
  HEB: 'PRDT-v1-e7a1d3ba-da09-45e5-ab1b-f822cd01b3bf',
  CM: 'PRDT-v1-d7a7bcc2-a2cd-41fe-91d2-b6d355b3f68f',
  HICV: 'PRDT-v1-610f2cec-ac8b-4f00-abd2-83dbd74982da',
  TURKISH: 'PRDT-v1-ed952893-358b-4a70-b4bc-24fac624d5aa',
  EB: 'PRDT-v1-13844060-10f9-481b-bbd8-ce554f70422a',
  BB: 'PRDT-v1-028a15e5-acd2-4bfe-959a-19558e2894b6',
  FETCH: 'PRDT-v1-b30b35f0-42cb-4ba2-a1e0-ddd3572e5dc1',
  RAKUTEN: 'PRDT-v1-611dddad-353d-46c4-8139-3385ed742de6',
  BOOKING: 'PRDT-v1-a0ef33e9-5976-4e56-aed1-2d0d7cf940d5',
  CRATE_BARREL: 'PRDT-v1-30763e2e-7278-43ca-82b0-cbf6cf7351bb',
  KROGER: 'PRDT-v1-c3526cd6-e384-44ea-9fbb-1ca49a554b4e',
  SHELL: 'PRDT-v1-685fa920-e1d2-44cb-ad74-de5ba42f2b87',
} as const satisfies Record<keyof typeof MERCHANT_KEY, string>;

export type ProductUUID = (typeof PRODUCT_UUID)[keyof typeof PRODUCT_UUID];

const isProductUUID = (val: unknown): val is ProductUUID => {
  if (!val) return false;
  if (typeof val !== 'string') return false;
  if (!Object.values(PRODUCT_UUID).includes(val as ProductUUID)) return false;

  return true;
};

type LanguageDependentUrl = {
  [LanguageCode.English]: string;
  [LanguageCode.Spanish]: string;
};

function urlPair(en: string, es: string): LanguageDependentUrl {
  return {
    [LanguageCode.English]: en,
    [LanguageCode.Spanish]: es,
  };
}

export const ESIGN_POLICY = urlPair(
  DocumentUrl.IMPRINT_ESIGN,
  DocumentUrl.IMPRINT_ESIGN_ES
);

export const PRIVACY_NOTICE = urlPair(
  DocumentUrl.IMPRINT_PRIVACY,
  DocumentUrl.IMPRINT_PRIVACY_ES
);

export interface QRInfo {
  iosLink: string;
  androidLink: string;
  lpName: string;
}
interface MerchantInfo {
  productUUID: string;
  cardNetwork: CardNetwork;
  issuingBank: IssuingBank;
  cardholderAgreement: LanguageDependentUrl;
  infoSharingNotice: LanguageDependentUrl;
  rewardsTerms: LanguageDependentUrl;
  zendesk: LanguageDependentUrl;
  displaySignupLink: string;
  annualFee: string;
  qrInfo?: QRInfo;
  pageToNavigate: string;
  // If true, only allow access from /start?client_secret=..., not allow /{partner}/credit
  shouldBlockExternalApply?: boolean;
}

export const merchantList = {
  HH: {
    productUUID: PRODUCT_UUID.HH,
    cardNetwork: CardNetwork.Visa,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(DocumentUrl.HH_CHA_EN, DocumentUrl.HH_CHA_ES),
    infoSharingNotice: urlPair(DocumentUrl.HH_CISN_EN, DocumentUrl.HH_CISN_ES),
    rewardsTerms: urlPair(DocumentUrl.HH_TERMS_EN, DocumentUrl.HH_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.HH_EN, ZendeskUrl.HH_ES),
    displaySignupLink: 'imprint.co/hh',
    annualFee: '$0',
    qrInfo: MerchantQRInfo.HH,
    pageToNavigate: `/${MERCHANT_KEY.HH}/credit`,
  },
  WG: {
    productUUID: PRODUCT_UUID.WG,
    cardNetwork: CardNetwork.Mastercard,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(DocumentUrl.WG_CHA_EN, DocumentUrl.WG_CHA_ES),
    infoSharingNotice: urlPair(DocumentUrl.WG_CISN_EN, DocumentUrl.WG_CISN_ES),
    rewardsTerms: urlPair(DocumentUrl.WG_TERMS_EN, DocumentUrl.WG_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.WG_EN, ZendeskUrl.WG_ES),
    displaySignupLink: 'imprint.co/wow',
    annualFee: '$0',
    qrInfo: MerchantQRInfo.WG,
    /*
    This route URL was previously configured as 'westgateresorts' even though merchantKey
    is 'westgate'. Not sure why, so using previous string here instead of merchantKey to
    keep behavior the same
    */
    pageToNavigate: `/westgateresorts/credit`,
  },
  HEB: {
    productUUID: PRODUCT_UUID.HEB,
    cardNetwork: CardNetwork.Visa,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(
      DocumentUrl.HEB_CHA_EN,
      DocumentUrl.HEB_CHA_ES
    ),
    infoSharingNotice: urlPair(
      DocumentUrl.HEB_CISN_EN,
      DocumentUrl.HEB_CISN_ES
    ),
    rewardsTerms: urlPair(DocumentUrl.HEB_TERMS_EN, DocumentUrl.HEB_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.HEB_EN, ZendeskUrl.HEB_ES),
    displaySignupLink: 'imprint.co/heb',
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.HEB}/credit`,
  },
  CM: {
    productUUID: PRODUCT_UUID.CM,
    cardNetwork: CardNetwork.Visa,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(DocumentUrl.CM_CHA_EN, DocumentUrl.CM_CHA_ES),
    infoSharingNotice: urlPair(DocumentUrl.CM_CISN_EN, DocumentUrl.CM_CISN_ES),
    rewardsTerms: urlPair(DocumentUrl.CM_TERMS_EN, DocumentUrl.CM_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.CM_EN, ZendeskUrl.CM_ES),
    displaySignupLink: 'imprint.co/cm',
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.CM}/credit`,
  },
  HICV: {
    productUUID: PRODUCT_UUID.HICV,
    cardNetwork: CardNetwork.Mastercard,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(
      DocumentUrl.HICV_CHA_EN,
      DocumentUrl.HICV_CHA_ES
    ),
    infoSharingNotice: urlPair(
      DocumentUrl.HICV_CISN_EN,
      DocumentUrl.HICV_CISN_ES
    ),
    rewardsTerms: urlPair(DocumentUrl.HICV_TERMS_EN, DocumentUrl.HICV_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.HICV_EN, ZendeskUrl.HICV_ES),
    displaySignupLink: 'imprint.co/hicv',
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.HICV}/credit`,
  },
  TURKISH: {
    productUUID: PRODUCT_UUID.TURKISH,
    cardNetwork: CardNetwork.Visa,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(
      DocumentUrl.TURKISH_CHA_EN,
      DocumentUrl.TURKISH_CHA_ES
    ),
    infoSharingNotice: urlPair(
      DocumentUrl.TURKISH_CISN_EN,
      DocumentUrl.TURKISH_CISN_ES
    ),
    rewardsTerms: urlPair(
      DocumentUrl.TURKISH_TERMS_EN,
      DocumentUrl.TURKISH_TERMS_ES
    ),
    zendesk: urlPair(ZendeskUrl.TURKISH_EN, ZendeskUrl.TURKISH_ES),
    displaySignupLink: 'imprint.co/turkishairlines',
    annualFee: '$99',
    pageToNavigate: `/${MERCHANT_KEY.TURKISH}/premier/credit`,
  },
  EB: {
    productUUID: PRODUCT_UUID.EB,
    cardNetwork: CardNetwork.Mastercard,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(DocumentUrl.EB_CHA_EN, DocumentUrl.EB_CHA_ES),
    infoSharingNotice: urlPair(DocumentUrl.EB_CISN_EN, DocumentUrl.EB_CISN_ES),
    rewardsTerms: urlPair(DocumentUrl.EB_TERMS_EN, DocumentUrl.EB_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.EB_EN, ZendeskUrl.EB_ES),
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.EB}/credit`,
  },
  BB: {
    productUUID: PRODUCT_UUID.BB,
    cardNetwork: CardNetwork.Mastercard,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(DocumentUrl.BB_CHA_EN, DocumentUrl.BB_CHA_ES),
    infoSharingNotice: urlPair(DocumentUrl.BB_CISN_EN, DocumentUrl.BB_CISN_ES),
    rewardsTerms: urlPair(DocumentUrl.BB_TERMS_EN, DocumentUrl.BB_TERMS_ES),
    zendesk: urlPair(ZendeskUrl.BB_EN, ZendeskUrl.BB_ES),
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.BB}/credit`,
  },
  BOOKING: {
    productUUID: PRODUCT_UUID.BOOKING,
    cardNetwork: CardNetwork.Visa,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(
      DocumentUrl.BOOKING_CHA_EN,
      DocumentUrl.BOOKING_CHA_ES
    ), //TODO
    infoSharingNotice: urlPair(
      DocumentUrl.BOOKING_CISN_EN,
      DocumentUrl.BOOKING_CISN_ES
    ),
    rewardsTerms: urlPair(
      DocumentUrl.BOOKING_TERMS_EN,
      DocumentUrl.BOOKING_TERMS_ES
    ),
    zendesk: urlPair(ZendeskUrl.BOOKING_EN, ZendeskUrl.BOOKING_ES),
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.BOOKING}/credit`,
    shouldBlockExternalApply: true,
  },
  CRATE_BARREL: {
    productUUID: PRODUCT_UUID.CRATE_BARREL,
    cardNetwork: CardNetwork.Visa, //TODO
    issuingBank: IssuingBank.FEB, //TODO
    cardholderAgreement: urlPair(
      DocumentUrl.DEMO_TERMS,
      DocumentUrl.DEMO_TERMS
    ), //TODO
    infoSharingNotice: urlPair(DocumentUrl.DEMO_CISN, DocumentUrl.DEMO_CISN), //TODO
    rewardsTerms: urlPair(DocumentUrl.DEMO_TERMS, DocumentUrl.DEMO_TERMS), //TODO
    zendesk: urlPair(ZendeskUrl.DEFAULT, ZendeskUrl.DEFAULT), //TODO
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.CRATE_BARREL}/credit`,
  },
  RAKUTEN: {
    productUUID: PRODUCT_UUID.RAKUTEN,
    cardNetwork: CardNetwork.Amex,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(
      DocumentUrl.RAKUTEN_CHA_EN,
      DocumentUrl.RAKUTEN_CHA_ES
    ),
    infoSharingNotice: urlPair(
      DocumentUrl.RAKUTEN_CISN_EN,
      DocumentUrl.RAKUTEN_CISN_ES
    ),
    rewardsTerms: urlPair(
      DocumentUrl.RAKUTEN_TERMS_EN,
      DocumentUrl.RAKUTEN_TERMS_ES
    ),
    zendesk: urlPair(ZendeskUrl.RAKUTEN_EN, ZendeskUrl.RAKUTEN_ES),
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.RAKUTEN}/credit`,
    shouldBlockExternalApply: true,
  },
  FETCH: {
    productUUID: PRODUCT_UUID.FETCH,
    cardNetwork: CardNetwork.Amex,
    issuingBank: IssuingBank.FEB,
    cardholderAgreement: urlPair(
      DocumentUrl.FETCH_CHA_EN,
      DocumentUrl.FETCH_CHA_ES
    ),
    infoSharingNotice: urlPair(
      DocumentUrl.FETCH_CISN_EN,
      DocumentUrl.FETCH_CISN_ES
    ),
    rewardsTerms: urlPair(
      DocumentUrl.FETCH_TERMS_EN,
      DocumentUrl.FETCH_TERMS_ES
    ),
    zendesk: urlPair(ZendeskUrl.FETCH_EN, ZendeskUrl.FETCH_ES),
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.FETCH}/credit`,
    shouldBlockExternalApply: true,
  },
  KROGER: {
    productUUID: PRODUCT_UUID.KROGER,
    cardNetwork: CardNetwork.Visa, //TODO
    issuingBank: IssuingBank.FEB, //TODO
    cardholderAgreement: urlPair(
      DocumentUrl.DEMO_TERMS,
      DocumentUrl.DEMO_TERMS
    ), //TODO
    infoSharingNotice: urlPair(DocumentUrl.DEMO_CISN, DocumentUrl.DEMO_CISN), //TODO
    rewardsTerms: urlPair(DocumentUrl.DEMO_TERMS, DocumentUrl.DEMO_TERMS), //TODO
    zendesk: urlPair(ZendeskUrl.DEFAULT, ZendeskUrl.DEFAULT), //TODO
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.KROGER}/credit`,
  },
  SHELL: {
    productUUID: PRODUCT_UUID.SHELL,
    cardNetwork: CardNetwork.Visa, //TODO
    issuingBank: IssuingBank.FEB, //TODO
    cardholderAgreement: urlPair(
      DocumentUrl.DEMO_TERMS,
      DocumentUrl.DEMO_TERMS
    ), //TODO
    infoSharingNotice: urlPair(DocumentUrl.DEMO_CISN, DocumentUrl.DEMO_CISN), //TODO
    rewardsTerms: urlPair(DocumentUrl.DEMO_TERMS, DocumentUrl.DEMO_TERMS), //TODO
    zendesk: urlPair(ZendeskUrl.DEFAULT, ZendeskUrl.DEFAULT), //TODO
    displaySignupLink: '', //TODO
    annualFee: '$0',
    pageToNavigate: `/${MERCHANT_KEY.SHELL}/credit`,
  },
} as const satisfies Record<keyof typeof MERCHANT_KEY, MerchantInfo>;

export const merchantInfoByProductUUID: Record<string, MerchantInfo> =
  Object.entries(merchantList).reduce(
    (acc, [_key, val]) => {
      return {
        ...acc,
        [val.productUUID]: val,
      };
    },
    {} as Record<string, MerchantInfo>
  );

export const getCustomerSessionFromClientSecret = async (
  clientSecret: string
): Promise<API.Response<'/v1/sessions'> | null> => {
  const [, baseURL] = getEnv(window.location.hostname.toLowerCase());

  try {
    type customerSessionResponse = API.Response<'/v1/sessions'>;

    const res = await fetch(`${baseURL}${URL.CUSTOMER_SESSION}`, {
      headers: {
        [HEADERS.clientSecret]: clientSecret,
        [HEADERS.sessionId]: store.getState().apiScreenScreenSelector.sessionID,
      },
    });
    const customerSession = (await res.json()) as customerSessionResponse;

    if (!isProductUUID(customerSession.product_uuid)) return null;

    return customerSession;
  } catch (err) {
    console.log(err);
    return null;
  }
};
